import React from 'react';
import {
  Bar,
  BarChart as ReactBarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import styled from '@emotion/styled';
import { RechartsDevtools } from '@recharts/devtools';

interface ExternalTooltipProps {
  content: React.ReactNode | any;
  cursor?: boolean | object;
}
interface BarChartProps {
  isAnimationActive?: boolean;
  dataKeys?: string[];
  strokes?: string[];
  fills?: string[];
  data: Record<string, any>[];
  xAxisDataKey: string;
  aspect?: number;
  minHeight?: number;
  showLegend?: boolean;
  tooltipLabel?: string[];
  barRadius?: number | [number, number, number, number];
  externalTooltip?: ExternalTooltipProps;
  yLabel?: string;
  xLabel?: string;
  isCategorical?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  dataKeys,
  strokes,
  fills,
  data,
  xAxisDataKey,
  externalTooltip,
  isAnimationActive = true,
  aspect = 1.618,
  minHeight,
  showLegend = true,
  tooltipLabel = ['Value'],
  barRadius = [8, 8, 0, 0],
  yLabel,
  xLabel,
  isCategorical = false,
}) => {
  return (
    <ResponsiveContainer
      width='100%'
      aspect={aspect}
      maxHeight={500}
      minHeight={minHeight}
    >
      <ReactBarChart
        responsive
        data={data}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 25,
        }}
        barGap={8}
        barCategoryGap='20%'
      >
        <CartesianGrid
          strokeDasharray={'5 5'}
          fill={'#ffffff'}
          stroke={'#E2DED3'}
        />
        <XAxis
          dataKey={xAxisDataKey}
          interval={'preserveEnd'}
          tickMargin={15}
          tickSize={0}
          stroke={'#E2DED3'}
          style={{
            fontSize: '10px',
            fill: '#5D665F',
            fontWeight: '700',
          }}
          label={
            xLabel && {
              // value: 'No of days in the month',
              value: xLabel,
              position: { x: 389, y: 54 },
              angle: 0,
              style: {
                fontSize: '10px',
                textAnchor: 'middle',
              },
            }
          }
        />

        <YAxis
          width='auto'
          tickSize={0}
          stroke='#E2DED3'
          tickMargin={10}
          // domain={[0, (dataMax: number) => Math.max(dataMax, 4)]}
          label={
            yLabel && {
              value: yLabel,
              position: { x: -8, y: 0 },
              angle: -90,
              offset: -5,
              style: {
                fontSize: '10px',
                //  textAnchor: 'middle'
              },
            }
          }
          style={{
            fontSize: '10px',
            fill: '#5D665F',
            fontWeight: '700',
          }}
          tick={{
            fill: '#5D665F',
            fontSize: 10,
            fontWeight: 700,
          }}
        />

        <Tooltip
          content={
            externalTooltip?.content ? (
              externalTooltip.content
            ) : (
              <CustomTooltip tooltipLabel={tooltipLabel} />
            )
          }
          cursor={externalTooltip?.cursor || { fill: 'rgba(14, 26, 19, 0.05)' }}
        />
        {showLegend && (
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px',
            }}
            iconType='circle'
          />
        )}
        {dataKeys &&
          dataKeys.map((key, index) => (
            <Bar
              key={key}
              fill={fills && fills[index] ? fills[index] : 'lilac'}
              dataKey={key}
              isAnimationActive={isAnimationActive}
              radius={barRadius}
              maxBarSize={100}
            >
              {isCategorical &&
                data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={fills && fills[index] ? fills[index] : 'lilac'}
                  />
                ))}
            </Bar>
          ))}
        <RechartsDevtools />
      </ReactBarChart>{' '}
    </ResponsiveContainer>
  );
};

export default BarChart;

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  tooltipLabel?: string[];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  tooltipLabel,
}) => {
  if (active && payload && payload.length) {
    return (
      <TooltipContainer>
        {payload.map((entry: any, index: number) => (
          <TooltipItem key={`item-${index}`}>
            <TooltipRow>
              <ColorDot color={entry.color} />
              <TooltipLabel>
                {tooltipLabel && tooltipLabel[index]
                  ? tooltipLabel[index]
                  : entry.name}
                :
              </TooltipLabel>
              <TooltipValue>{entry.value}</TooltipValue>
            </TooltipRow>
          </TooltipItem>
        ))}
      </TooltipContainer>
    );
  }
  return null;
};

const TooltipContainer = styled.div`
  background: white;
  border-radius: 6px;
  padding: 4px 12px;
  box-shadow: 0px 4px 8px 0px rgba(14, 26, 19, 0.1);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  display: flex;
  align-items: center;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorDot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  flex-shrink: 0;
`;

const TooltipLabel = styled.span`
  font-size: 10px;
  color: #5D665F;
  font-weight: 400;
`;

const TooltipValue = styled.span`
  font-size: 12px;
  color: #0E1A13;
  font-weight: 500;
`;
